import React, { useEffect, useState } from 'react';
import { AnimatePresence, motion, useReducedMotion, Variants } from 'framer-motion';
import { ArrowUpRightIcon } from 'lucide-react';
import { Logo } from './Logo';
import { GridDivider } from './GridDivider';
import { navItems, contacts, office } from '../data/navigation';
const EASE = [0.23, 1, 0.32, 1] as const;
// Drawer curve: quick start, long soft landing — reads as one continuous, weighted motion
const DRAWER = [0.32, 0.72, 0, 1] as const;
const panelVariants: Variants = {
  closed: {
    clipPath: 'inset(0 0 100% 0)',
    transition: {
      duration: 0.3,
      ease: [0.4, 0, 0.2, 1],
      delay: 0.1,
      when: 'afterChildren'
    }
  },
  open: {
    clipPath: 'inset(0 0 0% 0)',
    transition: {
      duration: 0.3,
      ease: DRAWER,
      when: 'beforeChildren'
    }
  }
};
const fadePanel: Variants = {
  closed: {
    opacity: 0,
    transition: {
      duration: 0.15
    }
  },
  open: {
    opacity: 1,
    transition: {
      duration: 0.15
    }
  }
};
const lineVariants: Variants = {
  closed: {
    scaleX: 0,
    transition: {
      duration: 0.15
    }
  },
  open: {
    scaleX: 1,
    transition: {
      duration: 0.3,
      ease: EASE
    }
  }
};
// Links rise into place one after another; on close they all fade together, quickly
const itemVariants: Variants = {
  closed: {
    opacity: 0,
    y: 12,
    filter: 'blur(3px)',
    transition: {
      duration: 0.15,
      ease: 'easeOut'
    }
  },
  open: (i: number) => ({
    opacity: 1,
    y: 0,
    filter: 'blur(0px)',
    transition: {
      duration: 0.3,
      delay: i * 0.05,
      ease: EASE
    }
  })
};
export function Header() {
  const [mobile, setMobile] = useState(false);
  const reduce = useReducedMotion();

  // Escape closes the menu; closing again on desktop resize keeps state honest
  useEffect(() => {
    if (!mobile) return;
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && setMobile(false);
    const mq = window.matchMedia('(min-width: 1024px)');
    const onMq = () => mq.matches && setMobile(false);
    window.addEventListener('keydown', onKey);
    mq.addEventListener('change', onMq);
    return () => {
      window.removeEventListener('keydown', onKey);
      mq.removeEventListener('change', onMq);
    };
  }, [mobile]);
  return <motion.header className="absolute inset-x-0 top-0 z-30" initial={reduce ? false : {
    opacity: 0,
    y: 14,
    filter: 'blur(4px)'
  }} animate={{
    opacity: 1,
    y: 0,
    filter: 'blur(0px)'
  }} transition={{
    duration: 0.9,
    delay: 0.1,
    ease: [0.22, 1, 0.36, 1]
  }} data-id="element-236" data-mp="1wc4pagdr2rrc">
      {/* Header bar stays on top of the panel and switches to the light theme while open */}
      <div className="relative z-50" data-id="element-237" data-mp="1cox7s9pb9691">
        <div className="mx-auto flex max-w-[1720px] items-center justify-between px-5 pb-5 pt-8 md:px-16 md:pb-6 md:pt-10 lg:px-24 lg:pb-7 lg:pt-12" data-id="element-238" data-mp="1cpzxieawd2nd">
          <Logo tone={mobile ? 'dark' : 'light'} data-id="element-239" data-mp="13n77sfhd4yzn" />

          <nav className="hidden items-center lg:flex" aria-label="Primary" data-id="element-240" data-mp="13q37a88uzvq7">
            <ul className="-mr-[20px] flex items-center" data-id="element-241" data-mp="2t3gfi8k3e3wl">
              {navItems.map(item => <li key={item.label} data-id="element-242" data-mp="2y1hxsr9p1z70">
                  <a href={item.href} className="link-soft flex h-[50px] items-center whitespace-nowrap px-[20px] font-accent text-[11px] uppercase tracking-[0.08em] text-cream" data-id="element-243" data-mp="meo6p7hxdjn0">
                    {item.label}
                  </a>
                </li>)}
            </ul>
          </nav>

          <MenuToggle open={mobile} onToggle={() => setMobile(m => !m)} data-id="element-244" data-mp="rc6jxa2z6l0t" />
        </div>
        <div className={`transition-opacity duration-300 ${mobile ? 'opacity-0' : 'opacity-100'}`} data-id="element-245" data-mp="153uq6wqjmf9v">
          <GridDivider tone="dark" data-id="element-246" data-mp="1059th7ai2dq6" />
        </div>
      </div>

      <AnimatePresence data-id="element-247" data-mp="1f3vg51ix3tn1">
        {mobile && <>
            {/* Dim + softly blur the page below the panel; tap to close */}
            <motion.button key="scrim" type="button" aria-label="Close menu" tabIndex={-1} onClick={() => setMobile(false)} initial={{
          opacity: 0
        }} animate={{
          opacity: 1,
          transition: {
            duration: 0.3,
            ease: 'easeOut'
          }
        }} exit={{
          opacity: 0,
          transition: {
            duration: 0.28,
            delay: 0.08,
            ease: 'easeOut'
          }
        }} className="fixed inset-0 z-30 cursor-default bg-navy/45 backdrop-blur-[2px] lg:hidden" data-id="element-248" data-mp="1a4s9xdr7bhdh" />

            {/* White panel unrolls from the top; on close the content fades first, then the panel rolls up */}
            <motion.nav key="panel" id="mobile-menu" aria-label="Mobile" variants={reduce ? fadePanel : panelVariants} initial="closed" animate="open" exit="closed" className="absolute inset-x-0 top-0 z-40 flex min-h-[60svh] flex-col bg-white text-ink shadow-[0_24px_60px_-30px_rgba(10,16,30,0.45)] will-change-[clip-path] lg:hidden" data-id="element-249" data-mp="1k8fp9nwbf57l">
              {/* Space for the header bar, then a hairline that draws across */}
              <div className="h-[86px] md:h-[100px]" data-id="element-250" data-mp="15hyng3frqda6" />
              <motion.div variants={reduce ? undefined : lineVariants} className="h-px w-full origin-left bg-mist-line" data-id="element-251" data-mp="xzideq3w61t8" />

              <ul className="mx-auto w-full max-w-[1720px] flex-1 px-9 pt-3 md:px-16" data-id="element-252" data-mp="sykwshyj5ftu">
                {navItems.map((item, i) => <motion.li key={item.label} custom={i} variants={reduce ? undefined : itemVariants} className="border-b border-mist-line" data-id="element-253" data-mp="k097cj9ego2z" data-mpi={i}>
                    <a href={item.href} onClick={() => setMobile(false)} className="group flex items-baseline justify-between py-5" data-id="element-254" data-mp="a9edaspwwk6z">
                      <span className="flex items-baseline gap-4" data-id="element-255" data-mp="17erzo7v6ty8">
                        <span className="w-6 font-accent text-[10px] tabular-nums tracking-[0.08em] text-ink/40" data-id="element-256" data-mp="3serdgffs5tjy">
                          {String(i + 1).padStart(2, '0')}
                        </span>
                        <span className="font-display text-[32px] leading-none tracking-[-0.025em] text-ink transition-colors duration-200 group-active:text-ink/60" data-id="element-257" data-mp="3dsase9xautqv">
                          {item.label}
                        </span>
                      </span>
                      <ArrowUpRightIcon aria-hidden="true" strokeWidth={1.25} className="h-4 w-4 text-ink/40 transition-transform duration-200 ease-out group-hover:-translate-y-0.5 group-hover:translate-x-0.5" data-id="element-258" data-mp="38tr6uammbjz1" />
                    </a>
                  </motion.li>)}
              </ul>

              {/* Contact details: email, phone, office — then a single contact action */}
              <motion.div custom={navItems.length} variants={reduce ? undefined : itemVariants} className="mx-auto w-full max-w-[1720px] px-9 pb-8 pt-7 md:px-16" data-id="element-259" data-mp="jd1uaq4z1c2s">
                <dl className="grid grid-cols-2 gap-x-6 gap-y-5" data-id="element-260" data-mp="28tf6iqhipn68">
                  <div data-id="element-261" data-mp="23v3z0mep3or6">
                    <dt className="font-accent text-[10px] uppercase tracking-[0.08em] text-ink/45" data-id="element-262" data-mp="23vm0bxg7st5y">General</dt>
                    <dd className="mt-1.5" data-id="element-263" data-mp="2iiyrc1osjr3q">
                      <a href={`mailto:${contacts[0].email}`} className="text-[13px] text-ink/80" data-id="element-264" data-mp="2im30zel0zf1s">
                        {contacts[0].email}
                      </a>
                    </dd>
                  </div>
                  <div data-id="element-265" data-mp="1kw86dfihjifg">
                    <dt className="font-accent text-[10px] uppercase tracking-[0.08em] text-ink/45" data-id="element-266" data-mp="1kx1fgn4x2k0r">Phone</dt>
                    <dd className="mt-1.5" data-id="element-267" data-mp="1yx2fk0dcxnf6">
                      <a href={office.phoneHref} className="whitespace-nowrap text-[13px] text-ink/80" data-id="element-268" data-mp="1ytwqgl4qaljg">
                        {office.phone}
                      </a>
                    </dd>
                  </div>
                  <div data-id="element-269" data-mp="369kylqqz17co">
                    <dt className="font-accent text-[10px] uppercase tracking-[0.08em] text-ink/45" data-id="element-270" data-mp="36ackg9urnqpr">Media</dt>
                    <dd className="mt-1.5" data-id="element-271" data-mp="13nyuvu06k87z">
                      <a href={`mailto:${contacts[1].email}`} className="text-[13px] text-ink/80" data-id="element-272" data-mp="13kt7cj779eed">
                        {contacts[1].email}
                      </a>
                    </dd>
                  </div>
                  <div data-id="element-273" data-mp="1ibtpjfxr0mnp">
                    <dt className="font-accent text-[10px] uppercase tracking-[0.08em] text-ink/45" data-id="element-274" data-mp="1ic21n5m1rnxu">Office</dt>
                    <dd className="mt-1.5 text-[13px] leading-[1.4] text-ink/80" data-id="element-275" data-mp="1mcdl4f2obi7q">
                      {office.address.map(line => <span key={line} className="block" data-id="element-276" data-mp="1rel3pni0dwj1">
                          {line}
                        </span>)}
                    </dd>
                  </div>
                </dl>
                <a href="/#contact" onClick={() => setMobile(false)} className="mt-7 flex h-[48px] w-full items-center justify-between border border-ink/25 px-5 font-accent text-[11px] uppercase tracking-[0.08em] text-ink" data-id="element-277" data-mp="218bl68fpx6p8">
                  Begin a conversation
                  <ArrowUpRightIcon aria-hidden="true" strokeWidth={1.25} className="h-3.5 w-3.5" data-id="element-278" data-mp="2ffy4k1oxe0vt" />
                </a>
              </motion.div>
            </motion.nav>
          </>}
      </AnimatePresence>
    </motion.header>;
}

/** Two hairlines that fold into a cross — cream on navy, ink once the panel is open. */
function MenuToggle({
  open,
  onToggle
}: {
  open: boolean;
  onToggle: () => void;
}) {
  const t = {
    duration: 0.25,
    ease: EASE
  };
  return <button type="button" onClick={onToggle} aria-label={open ? 'Close menu' : 'Open menu'} aria-expanded={open} aria-controls="mobile-menu" className={`relative -mr-2 flex h-10 w-10 items-center justify-center transition-colors duration-300 lg:hidden ${open ? 'text-ink' : 'text-cream'}`} data-id="element-279" data-mp="2u543rpnrjr3p">
      <span className="relative block h-[9px] w-[26px]" data-id="element-280" data-mp="1dq2uqfrgpp1d">
        <motion.span className="absolute left-0 top-0 block h-px w-full bg-current" animate={open ? {
        y: 4,
        rotate: 45
      } : {
        y: 0,
        rotate: 0
      }} transition={t} data-id="element-281" data-mp="1d2o0glvcjljx" />
        <motion.span className="absolute bottom-0 right-0 block h-px bg-current" animate={open ? {
        y: -4,
        rotate: -45,
        width: '100%'
      } : {
        y: 0,
        rotate: 0,
        width: '65%'
      }} transition={t} data-id="element-282" data-mp="1msgz6mae466r" />
      </span>
    </button>;
}